import React from "react";
import "./facilityCell.scss";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  InformationPopUp,
  ConfigProviderComponent,
  SelectComponent,
  antdTheme,
} from "..";
import { Cell } from "../../../state/features/cellListReducer";

interface FacilityCellProps {
  showCell?: boolean;
  cellHandler?: (value: string) => void;
  selectedCell?: any;
  cellList?: { loading?: boolean; data?: Cell[] };
  currentMenu?: { keyPath?: string[]; key?: string };
  showInfo?: boolean;
  // For a caller placing this inline in its own control row (e.g. Cell
  // Layout's Toolbar) alongside other unlabeled controls, where a "Cell"
  // caption above just this one field would throw off the row's alignment.
  hideCellLabel?: boolean;
}

const FacilityCell = ({
  showCell,
  cellHandler = () => {},
  selectedCell,
  cellList,
  currentMenu,
  showInfo = true,
  hideCellLabel = false,
}: FacilityCellProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathArr = location?.pathname?.split("/") ?? [];
  let path = pathArr?.[pathArr?.length - 1] || "";
  const currentTab = currentMenu?.keyPath?.[currentMenu?.keyPath?.length - 1]
  const tabSelection = currentMenu?.key;

  const fc = 'runDataExplorer.facility-cell';
  const pathContent: any = {
    no_information: {
      title: t(`${fc}.no-information.title`),
      content: t(`${fc}.no-information.body`),
    },
    live_services: {
      ml_controller: {
        ml_controller_dashboard: {
          title: t(`${fc}.ml-controller.dashboard.title`),
          content: (
            <p>
              <b>Dashboard:</b> {t(`${fc}.ml-controller.dashboard.intro`)}
              <br /><br />
              <b>ML Controller:</b> {t(`${fc}.ml-controller.description`)}
            </p>
          ),
        },
        ml_controller_charts: {
          title: t(`${fc}.ml-controller.charts.title`),
          content: (
            <p>
              <b>Charts:</b> {t(`${fc}.ml-controller.charts.intro`)}
              <br /><br />
              <b>ML Controller:</b> {t(`${fc}.ml-controller.description`)}
            </p>
          ),
        },
        ml_controller_summary: {
          title: t(`${fc}.ml-controller.summary.title`),
          content: (
            <p>
              <b>Summary:</b> {t(`${fc}.ml-controller.summary.intro`)}
              <br /><br />
              <b>ML Controller:</b> {t(`${fc}.ml-controller.description`)}
            </p>
          ),
        },
      },
      ml_predictor: {
        ml_predictor_dashboard: {
          title: t(`${fc}.ml-predictor.dashboard.title`),
          content: (
            <p>
              <b>Dashboard:</b> {t(`${fc}.ml-predictor.dashboard.intro`)}
              <br /><br />
              <b>ML Predictor:</b> {t(`${fc}.ml-predictor.description`)}
            </p>
          ),
        },
        ml_predictor_charts: {
          title: t(`${fc}.ml-predictor.charts.title`),
          content: (
            <p>
              <b>Charts:</b> {t(`${fc}.ml-predictor.charts.intro`)}
              <br /><br />
              <b>ML Predictor:</b> {t(`${fc}.ml-predictor.description`)}
            </p>
          ),
        },
        ml_predictor_summary: {
          title: t(`${fc}.ml-predictor.summary.title`),
          content: (
            <p>
              <b>Summary:</b> {t(`${fc}.ml-predictor.summary.intro`)}
              <br /><br />
              <b>ML Predictor:</b> {t(`${fc}.ml-predictor.description`)}
            </p>
          ),
        },
      },
      rules_controller: {
        rules_controller_dashboard: {
          title: t(`${fc}.rules-controller.dashboard.title`),
          content: (
            <p>
              <b>Dashboard:</b> {t(`${fc}.rules-controller.dashboard.intro`)}
              <br /><br />
              <b>Rules Controller:</b> {t(`${fc}.rules-controller.description`)}
            </p>
          ),
        },
        rules_controller_charts: {
          title: t(`${fc}.rules-controller.charts.title`),
          content: (
            <p>
              <b>Charts:</b> {t(`${fc}.rules-controller.charts.intro`)}
              <br /><br />
              <b>Rules Controller:</b> {t(`${fc}.rules-controller.description`)}
            </p>
          ),
        },
        rules_controller_summary: {
          title: t(`${fc}.rules-controller.summary.title`),
          content: (
            <p>
              <b>Summary:</b> {t(`${fc}.rules-controller.summary.intro`)}
              <br /><br />
              <b>Rules Controller:</b> {t(`${fc}.rules-controller.description`)}
            </p>
          ),
        },
      },
      training_wheels: {
        training_wheels_dashboard: {
          title: t(`${fc}.training-wheels.dashboard.title`),
          content: (
            <p>
              <b>Dashboard:</b> {t(`${fc}.training-wheels.dashboard.intro`)}
              <br /><br />
              <b>Training Wheels:</b> {t(`${fc}.training-wheels.description`)}
            </p>
          ),
        },
        training_wheels_charts: {
          title: t(`${fc}.training-wheels.charts.title`),
          content: (
            <p>
              <b>Charts:</b> {t(`${fc}.training-wheels.charts.intro`)}
              <br /><br />
              <b>Training Wheels:</b> {t(`${fc}.training-wheels.description`)}
            </p>
          ),
        },
        training_wheels_summary: {
          title: t(`${fc}.training-wheels.summary.title`),
          content: (
            <p>
              <b>Summary:</b> {t(`${fc}.training-wheels.summary.intro`)}
              <br /><br />
              <b>Training Wheels:</b> {t(`${fc}.training-wheels.description`)}
            </p>
          ),
        },
      },
      matrix_controller: {
        matrix_controller_dashboard: {
          title: t(`${fc}.matrix-controller.dashboard.title`),
          content: (
            <p>
              <b>Dashboard:</b> {t(`${fc}.matrix-controller.dashboard.intro`)}
              <br /><br />
              <b>Matrix Controller:</b> {t(`${fc}.matrix-controller.description`)}
            </p>
          ),
        },
        matrix_controller_output: {
          title: t(`${fc}.matrix-controller.output.title`),
          content: (
            <p>
              <b>Output:</b> {t(`${fc}.matrix-controller.output.intro`)}
              <br /><br />
              <b>Matrix Controller:</b> {t(`${fc}.matrix-controller.description`)}
            </p>
          ),
        },
        matrix_controller_equipment: {
          title: t(`${fc}.matrix-controller.equipment.title`),
          content: (
            <p>
              <b>Equipment:</b> {t(`${fc}.matrix-controller.equipment.intro`)}
              <br /><br />
              <b>Matrix Controller:</b> {t(`${fc}.matrix-controller.description`)}
            </p>
          ),
        },
      },
    },
  };

  const currentTitleObj =
    path !== "live_services"
      ? pathContent?.["no_information"]
      : pathContent?.[path]?.[currentTab!]?.[tabSelection!];
  const content = currentTitleObj?.content;
  const title = currentTitleObj?.title;

  return (
    <div>
      <ConfigProviderComponent theme={antdTheme}>
        <div className="fcDropdownContainer">
          {showCell &&
            <div className="fcItem">
              {!hideCellLabel && <div className="cellLabel">{t('runDataExplorer.facility-cell.cellLabel')}</div>}
              <SelectComponent
                placeholder={t('runDataExplorer.facility-cell.selectCellPlaceholder')}
                className="cellSelect"
                onChangeEvent={cellHandler}
                allowClear
                value={selectedCell?.text}
                loading={cellList?.loading ?? false}
                options={cellList?.data ?? []}
                disabledOptions={["Loading", "Select a Facility"]}
                filterSort={undefined}
              />
          </div>
          }
          {showInfo && <div className="infoIcon">
            <InformationPopUp title={title} content={content} />
          </div>}
        </div>
      </ConfigProviderComponent>
    </div>
  );
};

export default FacilityCell;
